/**
 * Forma dei display value restituiti dall'API, comune alle due sorgenti
 * (`.FT` nella lista modelle, `.Features` nel dettaglio contatto, `/contacts/features/{ID}` nel drawer):
 *
 *   Conversion  → mappa metrica → valore: { cm: '175', in: "5' 9\"" }
 *   Multichoice → array di stringhe: ['running', 'swimming']
 *   altri tipi  → scalare: 'Brown', 'false', 4
 *
 * Il dettaglio contatto incapsula il display value in { kind, values | value }:
 * l'involucro viene rimosso alla frontiera da `unwrapDisplayValue`.
 */

/**
 * Rimuove l'involucro { kind, values | value } del dettaglio contatto.
 * Si basa sulla presenza delle chiavi e non su `kind`, che duplica il TypeName dell'item.
 */
const unwrapDisplayValue = (displayValue) => {
    if (!displayValue || typeof displayValue !== 'object' || Array.isArray(displayValue)) {
        return displayValue;
    }
    if ('values' in displayValue) {
        return displayValue.values;
    }
    if ('value' in displayValue) {
        return displayValue.value;
    }
    return displayValue;
};

/**
 * Main features di un contatto: array di oggetti in entrambe le sorgenti, ma con
 * nomi diversi — `.FT` nella lista modelle, `.Features` nel dettaglio contatto.
 * (la vecchia `.FeatNwMain` del dettaglio non va più usata)
 */
const getContactMainFeatures = (contact) => (contact && (contact.FT || contact.Features)) || [];

/** Testo di un display value senza unità di misura: array e mappe diventano un elenco. */
const getPlainDisplayValue = (displayValue, separator = ' - ') => {
    if (displayValue === null || displayValue === undefined) {
        return '';
    }
    if (Array.isArray(displayValue)) {
        return displayValue.join(separator);
    }
    if (typeof displayValue === 'object') {
        return Object.values(displayValue).join(separator);
    }
    return String(displayValue);
};

/** Genere del contatto: `G` nella lista modelle, `GenderName` nel dettaglio contatto. */
const getContactGender = (contact) => (contact && (contact.G || contact.GenderName)) || null;

/**
 * Una feature gender specific va nascosta al contatto di genere diverso.
 * Servono entrambi i dati: se la feature è generica (`GenderSpecificName` assente)
 * o il genere del contatto non è noto, la feature resta visibile.
 */
const hiddenByGender = (feature, contact) => {
    const featureGender = feature && feature.GenderSpecificName;
    const contactGender = getContactGender(contact);
    if (!featureGender || !contactGender) {
        return false;
    }
    return featureGender.toLowerCase() !== contactGender.toLowerCase();
};

const wrongGenderSpecificMetric = (metricName, gender) => {
    if (!gender) {
        return false;
    }
    if (metricName === 'USW' && gender === 'Male') {
        return true;
    }
    if (metricName === 'USM' && gender === 'Female') {
        return true;
    }
    return false;
};

// function translateGender(genderEnum) {
//   return genderEnum === 2 ? "M" : "F";
// }

/**
 * Preferenza metrica dell'utente per una conversion, o null se non configurata.
 * L'aggancio è per ID: due conversion table possono condividere lo stesso Name.
 */
const getMetricPreference = (conversionId, featuresIndex) => {
    if (!conversionId || !featuresIndex) {
        return null;
    }
    return featuresIndex.metricPrefByConversionId.get(conversionId) || null;
};

/**
 * Prima metrica configurata dall'utente per una conversion, o null.
 * Con `gender` è la prima **mostrabile** a quel contatto: senza, la prima in assoluto
 * (i filtri di ricerca non hanno un contatto a cui riferirsi).
 */
const getPreferredMetric = (conversionId, featuresIndex, { gender } = {}) => {
    const preference = getMetricPreference(conversionId, featuresIndex);
    if (!preference) {
        return null;
    }
    return preference.metrics.find((metric) => !wrongGenderSpecificMetric(metric, gender)) || null;
};

/**
 * Valori nelle metriche configurate dall'utente, con unità di misura: "175 cm • 5'9 in".
 * I display value non-conversion non hanno metriche e vengono resi come testo.
 * `metricsLimit` restringe alle prime N metriche (1 = solo la primaria).
 * `fallbackToAllMetrics` ripiega su tutte le metriche della conversion quando nessuna delle configurate
 * è mostrabile: serve a chi deve comunque presentare un valore leggibile, non a chi mostra un dato salvato.
 */
function getDisplayValues(
    displayValue,
    conversionId,
    featuresIndex,
    { separator = '•', gender, metricsLimit, fallbackToAllMetrics } = {}
) {
    const isMetricMap = displayValue && typeof displayValue === 'object' && !Array.isArray(displayValue);
    if (!isMetricMap || !conversionId) {
        return getPlainDisplayValue(displayValue);
    }
    const preference = getMetricPreference(conversionId, featuresIndex);
    if (!preference) {
        return getPlainDisplayValue(displayValue);
    }

    const formatMetrics = (metricNames) => {
        const ret = [];
        for (const metric of metricNames) {
            if (wrongGenderSpecificMetric(metric, gender)) {
                continue;
            }
            // metrica configurata ma assente nel display value: si salta e si prosegue
            if (displayValue[metric] !== undefined) {
                ret.push(`${displayValue[metric]} ${metric}`);
            }
        }
        return ret.join(separator);
    };

    const metrics = metricsLimit ? preference.metrics.slice(0, metricsLimit) : preference.metrics;
    const preferredValues = formatMetrics(metrics);
    if (!preferredValues && fallbackToAllMetrics) {
        return formatMetrics(Object.keys(displayValue));
    }
    return preferredValues;
}

/** Valore nella sola metrica primaria dell'utente, con unità: "175 cm". */
const getPrimaryDisplayValue = (displayValue, conversionId, featuresIndex, options = {}) =>
    getDisplayValues(displayValue, conversionId, featuresIndex, { ...options, metricsLimit: 1 });

/**
 * Display value di una conversion come `[{ metric, value }]`, nell'ordine delle metriche
 * visibili dell'utente. Lo switch metrica del drawer indicizza questo array per posizione,
 * quindi le metriche escluse vanno tolte qui: un array più corto della lista di preferenze
 * è normale e l'indice va riportato dentro con `getVisibleMetricIndex`.
 */
function getSortedValues(displayValue, conversionId, featuresIndex, { gender } = {}) {
    if (!displayValue || typeof displayValue !== 'object' || Array.isArray(displayValue)) {
        return [];
    }
    const preference = getMetricPreference(conversionId, featuresIndex);
    const metrics = preference ? preference.metrics : Object.keys(displayValue);
    const ret = [];
    for (const metric of metrics) {
        if (wrongGenderSpecificMetric(metric, gender)) {
            continue;
        }
        // vedi getDisplayValues: le metriche assenti si saltano, non fanno fallback
        if (displayValue[metric] !== undefined) {
            ret.push({ metric, value: displayValue[metric] });
        }
    }
    return ret;
}

/**
 * Indice della metrica da mostrare in un array di `getSortedValues`: quello scelto dall'utente
 * riportato dentro l'array, che può essere più corto della lista delle preferenze.
 * Vale -1 quando non c'è nessuna metrica da mostrare.
 */
const getVisibleMetricIndex = (sortedValues, metricIndex) => Math.min(metricIndex, sortedValues.length - 1);

/**
 * Valore grezzo (senza unità) di una main feature del payload `.FT`, nella metrica
 * primaria dell'utente. Il payload porta direttamente il ConversionID (`C`).
 *
 * Passa da `getSortedValues` per costruzione: il riepilogo e il drawer devono mostrare
 * la stessa metrica, e tenerli su due selezioni separate li ha già fatti divergere
 * (il riepilogo mostrava la taglia USM a una modella, il drawer la USW).
 */
const getMainFeatureValue = (mainFeature, featuresIndex, { gender } = {}) => {
    const { C: conversionId, DV: displayValue } = mainFeature;
    if (!conversionId) {
        return getPlainDisplayValue(displayValue);
    }
    const sortedValues = getSortedValues(displayValue, conversionId, featuresIndex, { gender });
    return sortedValues.length ? sortedValues[0].value : '';
};

/** ConversionValues raggruppati per Key, con il display value in forma di mappa metrica → valore. */
const groupConversionValues = (conversionValues) => {
    const groupedByKey = new Map();
    for (const conversionValue of conversionValues) {
        if (!groupedByKey.has(conversionValue.Key)) {
            groupedByKey.set(conversionValue.Key, {});
        }
        groupedByKey.get(conversionValue.Key)[conversionValue.Metric] = conversionValue.Value;
    }
    return [...groupedByKey].map(([Key, DisplayValue]) => ({ Key, DisplayValue }));
};

export {
    getContactGender,
    getContactMainFeatures,
    getDisplayValues,
    getMainFeatureValue,
    getMetricPreference,
    getPlainDisplayValue,
    getPreferredMetric,
    getPrimaryDisplayValue,
    getSortedValues,
    getVisibleMetricIndex,
    groupConversionValues,
    hiddenByGender,
    unwrapDisplayValue,
    wrongGenderSpecificMetric,
};
